import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { WasteManifestSource } from "generated-graphql/graphql";
import { prettyPrintDaysFromInt } from "util/dates";

import { SOURCE_LABEL, SOURCE_ORDER } from "./manifestSourceDisplay";
import { getSourceAccent, SOURCE_LETTER } from "./SourceLetterBox";

export interface SourcesCellProps {
  availableSources: readonly WasteManifestSource[];
  /**
   * Days since shipment with no e-Manifest record yet. Shown inline next to a
   * missing e-Manifest (e.g. "e-Manifest: missing (52 days)") so this column
   * subsumes the standalone "Days awaiting e-Manifest" column.
   */
  daysAwaitingEManifest?: number | null;
  /**
   * Descriptive document processing status (e.g. "reviewed", "needs review",
   * "import failed"). When provided and the Document source is present, the
   * tooltip shows it in place of "present" (e.g. "Document: reviewed"), letting
   * this column subsume the standalone Document column. `null`/undefined falls
   * back to present/missing.
   */
  documentStatusLabel?: string | null;
  /** The pills to show, in order. A bill of lading passes its two sources; it is never filed in e-Manifest. */
  sourceOrder?: readonly WasteManifestSource[];
}

/**
 * Three small pills (`E` / `V` / `D`) stating which data pillars are on file
 * for a given manifest. The tooltip lists each source as present/missing and,
 * when the e-Manifest is missing, how many days it has been awaited.
 *
 * Why three letters instead of full words: the pills need to land in a 38px
 * grid row alongside the manifest tracking# and facility, and the alphabet
 * column is repeated identically on every row so a glance trains the user
 * fast.
 */
export function SourcesCell({
  availableSources,
  daysAwaitingEManifest,
  documentStatusLabel,
  sourceOrder = SOURCE_ORDER,
}: SourcesCellProps) {
  const theme = useTheme();
  const present = new Set(availableSources);

  const tooltipLines: string[] = sourceOrder.map((source) => {
    const isPresent = present.has(source);

    // Document line surfaces the processing status (reviewed / needs review /
    // import failed / attached) in place of a bare "present"/"missing" —
    // including when the document is NOT in availableSources: an attached but
    // unreviewed document is excluded as a source, and "missing" would be
    // factually wrong.
    if (source === WasteManifestSource.ManifestDocument && documentStatusLabel) {
      return `${SOURCE_LABEL[source]}: ${documentStatusLabel}`;
    }

    const status = isPresent ? "present" : "missing";
    const line = `${SOURCE_LABEL[source]}: ${status}`;
    if (
      source === WasteManifestSource.EManifest &&
      status === "missing" &&
      daysAwaitingEManifest != null
    ) {
      return `${line} (${prettyPrintDaysFromInt(daysAwaitingEManifest)})`;
    }
    return line;
  });

  return (
    <Tooltip
      title={
        <Stack spacing={0.25}>
          {tooltipLines.map((line) => (
            <Typography key={line} variant="caption" sx={{ fontSize: 11 }}>
              {line}
            </Typography>
          ))}
        </Stack>
      }
      placement="right"
      enterDelay={400}
    >
      <Stack direction="row" spacing={0.5} alignItems="center">
        {sourceOrder.map((source) => {
          const isPresent = present.has(source);

          // Present pills tint in their source's accent color (same recipe as
          // the drawer's SourceLetterBox) so the letter and the per-field
          // provenance marks read as one system.
          const accent = getSourceAccent(theme, source);

          const bg = isPresent ? alpha(accent, 0.12) : "transparent";
          const fg = isPresent ? accent : theme.palette.text.disabled;
          const border = isPresent ? alpha(accent, 0.5) : theme.palette.divider;

          return (
            <Box
              key={source}
              aria-label={`${SOURCE_LABEL[source]}: ${isPresent ? "present" : "missing"}`}
              sx={{
                width: 20,
                height: 20,
                borderRadius: 0.75,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 500,
                backgroundColor: bg,
                color: fg,
                border: `1px solid ${border}`,
              }}
            >
              {SOURCE_LETTER[source]}
            </Box>
          );
        })}
      </Stack>
    </Tooltip>
  );
}
